import type { PrintLetter, PrintLettersRecord } from '@/types/printLetter';

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** The record's `data`, from either `{ record: { data } }` or the data itself. */
export function printLettersData(payload: unknown): Record<string, unknown> | null {
  const root = asRecord(payload);
  const record = asRecord(root?.record);
  if (record) return asRecord(record.data);
  return root;
}

/** Parse a `print-letters` record. Rows without an id or a template id are dropped. */
export function printLettersFromRecord(payload: unknown): PrintLettersRecord | null {
  const data = printLettersData(payload);
  if (!data) return null;
  const rows = Array.isArray(data.letters) ? data.letters : [];
  const letters: PrintLetter[] = [];
  const seen = new Set<string>();
  rows.forEach((row) => {
    const source = asRecord(row);
    if (!source) return;
    const id = text(source.id);
    const postGridTemplateId = text(source.postGridTemplateId ?? source.postgridTemplateId);
    if (!id || !postGridTemplateId || seen.has(id)) return;
    seen.add(id);
    letters.push({
      id,
      name: text(source.name) || 'Print letter',
      postGridTemplateId,
      isEnabled: source.isEnabled === true || source.enabled === true
    });
  });
  return {
    isEnabled: data.isEnabled === true,
    letters,
    updatedAt: text(data.updatedAt) || null,
    updatedBy: text(data.updatedBy) || null
  };
}

/** Letters staff can print: the record is on and the letter is on. */
export function enabledPrintLetters(record: PrintLettersRecord | null | undefined): PrintLetter[] {
  if (!record?.isEnabled) return [];
  return record.letters.filter((letter) => letter.isEnabled);
}

/**
 * The clinic record applies whenever the clinic has one, including when it is off.
 * The account record applies only when the clinic has no record.
 */
export function selectPrintLetters(
  clinic: PrintLettersRecord | null,
  account: PrintLettersRecord | null
): { source: 'clinic' | 'account'; record: PrintLettersRecord; letters: PrintLetter[] } | null {
  if (clinic) {
    const letters = enabledPrintLetters(clinic);
    return letters.length ? { source: 'clinic', record: clinic, letters } : null;
  }
  const letters = enabledPrintLetters(account);
  return account && letters.length ? { source: 'account', record: account, letters } : null;
}

/**
 * The data to save after an admin edit. Only names and on/off change; ids and template ids stay as
 * ops set them, and any field this app does not know about is kept.
 */
export function printLettersSaveData(
  raw: Record<string, unknown>,
  edits: Pick<PrintLetter, 'id' | 'name' | 'isEnabled'>[],
  updatedBy: string,
  now = new Date()
): Record<string, unknown> {
  const rows = Array.isArray(raw.letters) ? raw.letters : [];
  const letters = rows.map((row) => {
    const source = asRecord(row);
    if (!source) return row;
    const edit = edits.find((item) => item.id === text(source.id));
    if (!edit) return row;
    return { ...source, name: edit.name.trim() || text(source.name), isEnabled: edit.isEnabled };
  });
  return { ...raw, letters, updatedAt: now.toISOString(), updatedBy };
}
